import React, { createContext, useCallback, useEffect, useRef, useState } from 'react'

import { useStaticQuery, graphql } from 'gatsby'

import {
	addBundleToStrapiCart,
	addProductToStrapiCart,
	createStrapiCartID,
	decreaseBundleToStrapiCart,
	decreaseProductToStrapiCart,
	deleteStrapiCart,
	getBundleFromStrapi,
	getProductFromStrapi,
	getStrapiCart,
	increaseBundleToStrapiCart,
	increaseProductToStrapiCart,
	removeBundleToStrapiCart,
	removeProductToStrapiCart
} from '../utils/strapi'
import useWindowWidth from '../hooks/useWindowWidth'
import { addCustomerToNewsletter } from '../utils/newsletterUtils'
import { TrackAddProductToCartEvent } from '../utils/google-analytics'
import { resolveCartUid } from '../utils/cartSession'
import { applyQuoteToDeliveryOption } from '../utils/checkoutPayload'
import {
	reconcileStockAvailability,
	removeIneligibleComplimentaryProducts
} from '../utils/stockAvailability'
import { toast } from 'react-toastify'
import {
	CHECKOUT_MAX_LINE_ITEMS,
	CHECKOUT_MAX_COMPLIMENTARY_LINE_ITEMS,
	CHECKOUT_MAX_QUANTITY,
	getReservedVariantQuantity,
	canAddLine,
	canAddComplimentaryLine
} from '../utils/checkoutLimits'
import {
	COMPLIMENTARY_REASON,
	getComplimentaryReason
} from '../utils/complimentaryProducts'

export const AppContext = createContext()

const checkoutLimitToast = (message) =>
	toast.error(message, {
		position: 'top-right',
		autoClose: 10000,
		theme: 'dark'
	})

export const AppProvider = ({ children }) => {
	const data = useStaticQuery(graphql`
		query {
			strapiWerte {
				Versandkosten
				Versandkostenfrei_ab
				Gratis_Produkt_ab
			}
			strapiGratisProdukt {
				Gratis_Produkt {
					produkt {
						strapi_id
						Name
						slug
						kategorie {
							Name
						}
						marke {
							Name
						}
						Variante {
							groesse {
								Name
								strapi_id
							}
							Bildergalerie {
								url
							}
							Vorratsanzahl
						}
						Vorschaubild {
							url
						}
						edition {
							Name
						}
					}
					groesse {
						Name
						strapi_id
					}
					Anzahl
				}
			}
			strapiUpSalesProdukt {
				Produkt {
					produkt {
						strapi_id
						Name
						slug
						kategorie {
							Name
						}
						marke {
							Name
						}
						Variante {
							groesse {
								Name
								strapi_id
							}
							Bildergalerie {
								url
							}
							Vorratsanzahl
							Preis
							Preis_Angebot
						}
						Vorschaubild {
							url
						}
						edition {
							Name
						}
					}
					groesse {
						Name
						strapi_id
					}
					Anzahl
				}
			}
		}
	`)

	const [versandkostenValue] = useState(() => {
		return data.strapiWerte?.Versandkosten || 0
	})

	const [versandkostenfreiAb] = useState(() => {
		return data.strapiWerte?.Versandkostenfrei_ab || 0
	})

	const [gratisProduktAb] = useState(() => {
		return data.strapiWerte?.Gratis_Produkt_ab || 0
	})

	const [gratisProdukte] = useState(() => {
		return data.strapiGratisProdukt?.Gratis_Produkt || []
	})

	const [upSalesProdukte] = useState(() => {
		return data.strapiUpSalesProdukt?.Produkt || []
	})

	const isBrowser = typeof window !== `undefined`
	const windowWidth = useWindowWidth()

	// Browser Language
	const [language] = useState(() => {
		// if (isBrowser) {
		//     if (window.localStorage.browserLanguage) {
		//         return JSON.parse(window.localStorage.browserLanguage)
		//     } else {
		//         if (window.navigator.language === 'de-DE') {
		//             return 'de'
		//         }
		//         return 'en'
		//     }
		// } else {
		//     return 'en'
		// }

		return 'de'
	})

	// PopUp
	const [popUpIsOpen, setPopUpIsOpen] = useState(false)
	const [checkoutPopUpIsOpen, setCheckoutPopUpIsOpen] = useState(false)

	// Sidebar
	const [showNavbar, setShowNavbar] = useState(false)
	const [isSidebarOpen, setIsSidebarOpen] = useState(false)
	const handleSidebar = () => {
		setIsSidebarOpen(!isSidebarOpen)
	}

	const closeSidebar = () => {
		setIsSidebarOpen(false)
	}

	useEffect(() => {
		if (windowWidth < 1024) {
			if (isSidebarOpen) {
				setShowNavbar(false)
			}
		}
	}, [windowWidth, isSidebarOpen])

	// Cart
	const [cart, setCartState] = useState([])
	const cartRef = useRef(cart)
	const setCart = useCallback(value => {
		const next = typeof value === 'function' ? value(cartRef.current) : value
		cartRef.current = next
		setCartState(next)
	}, [])
	const createdCartUid = useRef(null)

	const [cartID, setCartID] = useState(() => {
		try {
			const stored = isBrowser ? JSON.parse(window.localStorage.getItem('CartID') || null) : null
			return typeof stored === 'string' && stored.trim() ? stored : null
		} catch {
			return null
		}
	})
	const cartIdRef = useRef(cartID)
	const cartActions = useRef(Promise.resolve())
	const cartMutationVersion = useRef(0)
	const queueCartAction = action => (...args) => {
		const result = cartActions.current.then(async () => {
			cartMutationVersion.current += 1
			try { return await action(...args) }
			catch {
				checkoutLimitToast('Der Warenkorb konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.')
				return false
			} finally { setCartIsLoading(false) }
		})
		cartActions.current = result.catch(() => {})
		return result
	}

	const resetCart = async () => {
		//  Delete Strapi Cart
		if (cartIdRef.current && !await deleteStrapiCart(cartIdRef.current)) return false
		cartIdRef.current = null
		createdCartUid.current = null
		setCartID(null)
		setCart([])
		try {
			if (isBrowser) window.localStorage.removeItem('CartID')
		} catch {
			// Storage may be disabled while an in-memory cart is still usable.
		}

		return true
	}

	useEffect(() => {
		// A newly created cart is already updated locally by the pending action.
		// Hydrating its earlier server snapshot would overwrite that action.
		if (cartID && createdCartUid.current === cartID) return
		let active = true
		const version = cartMutationVersion.current
		setCartIsLoading(true)

		const getCart = async () => {
			if (cartID) {
				const strapiCart = await getStrapiCart(cartID)
				if (!active || version !== cartMutationVersion.current) return

				if (strapiCart) {
					// ***** Get all products from cart
					const allProducts = strapiCart?.attributes?.Cart_Produkte || []

					const allProductsArray = allProducts.map((current) => ({
						product_type: 'product',
						product_strapi_id: current?.produkt?.data?.id,
						groesse_id: current?.groesse?.data?.id,
						product_count: current?.Anzahl
					}))

					const allProductsFromStrapi = await Promise.all(
						allProductsArray.map(async (currentProduct) => {
							const product = await getProductFromStrapi(
								currentProduct.product_strapi_id
							)

							if (!product) {
								return null
							}

							const productVariant = product.attributes?.Variante?.find(
								(item) => item.groesse?.data?.id === currentProduct.groesse_id
							)

							if (!productVariant) return null

							const vorratsanzahl = productVariant?.Vorratsanzahl ?? 0

							// Prüfen, ob tatsächlich genügend Vorrat existiert:
							// Wenn nicht ausreichend -> direkt aus Strapi entfernen und null zurückgeben
							if (vorratsanzahl < currentProduct.product_count) {
								await removeProductToStrapiCart(
									cartID,
									product.id,
									productVariant.groesse?.data?.attributes?.Name || ''
								)
								return null
							}

							// Wenn wir hier sind, existiert genügend Vorrat
							let productVariantImage =
								product.attributes?.Vorschaubild?.data?.attributes?.url

							if (productVariant?.Bildergalerie?.data?.length > 0) {
								productVariantImage =
									productVariant.Bildergalerie.data[0]?.attributes?.url
							}

							return {
								product_type: 'product',
								product_strapi_id: product.id,
								product_name: product.attributes?.Name,
								product_slug: product.attributes?.slug,
								product_edition:
									product.attributes?.edition?.data?.attributes?.Name,
								product_category:
									product.attributes?.kategorie?.data?.attributes?.Name,
								product_variant:
									productVariant?.groesse?.data?.attributes?.Name,
								product_variant_strapi_id: productVariant?.groesse?.data?.id,
								product_variant_price:
									productVariant?.Preis_Angebot || productVariant?.Preis,
								product_variant_normal_price: productVariant?.Preis,
								product_variant_discount: 0,
								product_count: currentProduct.product_count,
								product_image: productVariantImage
							}
						})
					)

					const validProductsFromStrapi = allProductsFromStrapi.filter(
						(p) => p !== null
					)

					// ***** Get all bundles from cart
					const allBundles = strapiCart?.attributes?.Cart_Bundles || []

					const allBundlesArray = allBundles.map((current) => ({
						product_type: 'bundle',
						product_strapi_id: current?.bundle?.data?.id,
						product_variant_one: current?.Produkt_1,
						product_variant_two: current?.Produkt_2,
						product_count: current?.Anzahl
					}))

					const allBundlesFromStrapi = await Promise.all(
						allBundlesArray.map(async (currentBundle) => {
							const strapi_bundle = await getBundleFromStrapi(
								currentBundle.product_strapi_id
							)

							if (!strapi_bundle) {
								return null
							}

							const bundleData = strapi_bundle?.attributes

							const produkt1 = bundleData?.Produkt?.[0]
							const produkt2 = bundleData?.Produkt?.[1]

							if (!produkt1 || !produkt2) {
								// Entfernen:
								await removeBundleToStrapiCart(
									cartID,
									currentBundle.product_strapi_id,
									currentBundle.product_variant_one,
									currentBundle.product_variant_two
								)
								return null
							}

							const produkt1Id = produkt1.produkt?.data?.id
							const produkt2Id = produkt2.produkt?.data?.id

							const variant1Name = currentBundle.product_variant_one
							const variant2Name = currentBundle.product_variant_two

							const produkt1Data = await getProductFromStrapi(produkt1Id)
							if (!produkt1Data) {
								await removeBundleToStrapiCart(
									cartID,
									currentBundle.product_strapi_id,
									variant1Name,
									variant2Name
								)
								return null
							}

							const produkt1Variant = produkt1Data.attributes?.Variante?.find(
								(v) => v.groesse?.data?.attributes?.Name === variant1Name
							)
							if (!produkt1Variant) {
								await removeBundleToStrapiCart(
									cartID,
									currentBundle.product_strapi_id,
									variant1Name,
									variant2Name
								)
								return null
							}

							const vorrat1 = produkt1Variant?.Vorratsanzahl ?? 0

							// Nun analog für Produkt 2
							let produkt2Variant = null
							let vorrat2 = 0

							// Prüfen, ob beide gleich sind
							if (produkt1Id === produkt2Id && variant1Name === variant2Name) {
								// Kombiniere Anzahl beider
								const gesamtAnzahl =
									currentBundle.product_count *
									(produkt1.Anzahl + produkt2.Anzahl)

								// Wenn vorrat1 nicht ausreicht, raus damit
								if (vorrat1 < gesamtAnzahl) {
									await removeBundleToStrapiCart(
										cartID,
										currentBundle.product_strapi_id,
										variant1Name,
										variant2Name
									)
									return null
								}
							} else {
								// Unterscheide Produkt 1 und 2
								const produkt2Data = await getProductFromStrapi(produkt2Id)
								if (!produkt2Data) {
									await removeBundleToStrapiCart(
										cartID,
										currentBundle.product_strapi_id,
										variant1Name,
										variant2Name
									)
									return null
								}
								produkt2Variant = produkt2Data.attributes?.Variante?.find(
									(v) => v.groesse?.data?.attributes?.Name === variant2Name
								)
								if (!produkt2Variant) {
									await removeBundleToStrapiCart(
										cartID,
										currentBundle.product_strapi_id,
										variant1Name,
										variant2Name
									)
									return null
								}

								vorrat2 = produkt2Variant?.Vorratsanzahl ?? 0

								const required1 = currentBundle.product_count * produkt1.Anzahl
								const required2 = currentBundle.product_count * produkt2.Anzahl

								if (vorrat1 < required1 || vorrat2 < required2) {
									// Wenn nicht ausreichend Vorrat -> entfernen
									await removeBundleToStrapiCart(
										cartID,
										currentBundle.product_strapi_id,
										variant1Name,
										variant2Name
									)
									return null
								}
							}

							// Wenn wir hier angekommen sind, gibt es genug Vorrat
							return {
								product_type: 'bundle',
								product_strapi_id: strapi_bundle.id,
								product_name: bundleData?.Name,
								product_edition: bundleData?.edition?.data?.attributes?.Name,
								product_count: currentBundle.product_count,
								product_one_strapi_id: produkt1Id,
								product_two_strapi_id: produkt2Id,
								product_one_name: produkt1.produkt?.data?.attributes?.Name,
								product_two_name: produkt2.produkt?.data?.attributes?.Name,
								product_one_edition:
									produkt1.produkt?.data?.attributes?.edition?.data?.attributes
										?.Name,
								product_two_edition:
									produkt2.produkt?.data?.attributes?.edition?.data?.attributes
										?.Name,
								product_one_anzahl: produkt1.Anzahl,
								product_two_anzahl: produkt2.Anzahl,
								product_variant_one: variant1Name,
								product_variant_two: variant2Name,
								product_variant_one_strapi_id:
									produkt1Variant?.groesse?.data?.id,
								product_variant_two_strapi_id:
									(produkt2Variant || produkt1Variant)?.groesse?.data?.id,
								product_variant_price: bundleData?.Preis,
								product_one_image:
									produkt1.produkt?.data?.attributes?.Vorschaubild?.data
										?.attributes?.url,
								product_two_image:
									produkt2.produkt?.data?.attributes?.Vorschaubild?.data
										?.attributes?.url
							}
						})
					)

					const validBundlesFromStrapi = allBundlesFromStrapi.filter(
						(b) => b !== null
					)

					// allItemsArray is an array with all products and bundles
					const allItemsArray = [
						...validProductsFromStrapi,
						...validBundlesFromStrapi
					]

					if (active && version === cartMutationVersion.current) setCart(allItemsArray)
				}
			}

			setCartIsLoading(false)
		}

		getCart().catch(() => {
			if (active) {
				setCartIsLoading(false)
				checkoutLimitToast('Der Warenkorb konnte nicht geladen werden. Bitte versuchen Sie es erneut.')
			}
		})

		return () => {
			active = false
		}
	}, [cartID])

	const [cartIsLoading, setCartIsLoading] = useState(false)

	const [cartProductCount, setCartProductCount] = useState(0)
	const [cartTotalPrice, setCartTotalPrice] = useState(0)
	const [deliveryOption, setDeliveryOption] = useState(null)

	const applyCheckoutQuote = useCallback((quote) => {
		if (!quote?.items) return
		setCart((currentCart) =>
			currentCart.map((item) => {
				const quoteLine = quote.items.find((line) => {
					if (item.product_type === 'bundle') {
						return (
							line.type === 'bundle' &&
							line.bundleId === item.product_strapi_id &&
							line.variantIds?.[0] === item.product_variant_one_strapi_id &&
							line.variantIds?.[1] === item.product_variant_two_strapi_id
						)
					}
					const expectedType =
						item.product_type === 'freeProduct'
							? 'complimentaryProduct'
							: 'product'
					return (
						line.type === expectedType &&
						line.productId === item.product_strapi_id &&
						line.variantId === item.product_variant_strapi_id
					)
				})

				return quoteLine
					? {
							...item,
							product_variant_price: quoteLine.unitPrice,
							product_variant_normal_price: quoteLine.normalUnitPrice,
							product_variant_discount: 0
						}
					: item
			})
		)
		setDeliveryOption((currentOption) =>
			applyQuoteToDeliveryOption(currentOption, quote)
		)
	}, [])

	const [deliveryForm, setDeliveryForm] = useState({
		email: '',
		telephone: '',
		deliveryCountry: 'DE',
		deliveryFirstName: '',
		deliveryLastName: '',
		deliveryCompany: '',
		deliveryStreet: '',
		deliveryAdditionalInformation: '',
		deliveryZipCode: '',
		deliveryCity: '',
		otherBillingAddress: false,
		billingCountry: 'DE',
		billingFirstName: '',
		billingLastName: '',
		billingCompany: '',
		billingStreet: '',
		billingAdditionalInformation: '',
		billingZipCode: '',
		billingCity: '',
		acceptTerms: false,
		acceptDHLCheck: false
	})

	const [isCaptureCheckout, setIsCaptureCheckout] = useState(false)
	const [captureCheckoutOrderPayerId, setCaptureCheckoutOrderPayerId] =
		useState(null)
	const [captureCheckoutOrderOrderId, setCaptureCheckoutOrderOrderId] =
		useState(null)
	const [captureCheckoutAttemptId, setCaptureCheckoutAttemptId] = useState(null)
	const [captureCheckoutAccessToken, setCaptureCheckoutAccessToken] =
		useState(null)
	const [captureCheckoutQuoteTotal, setCaptureCheckoutQuoteTotal] = useState(null)

	const [deliveryMessage, setDeliveryMessage] = useState('')

	const [couponDiscount, setCouponDiscount] = useState(null)
	const [couponDiscountID, setCouponDiscountID] = useState(null)
	const [couponDiscountVariant, setCouponDiscountVariant] = useState(null)
	const [couponName, setCouponName] = useState(null)
	const [deliveryDiscount, setDeliveryDiscount] = useState(null)
	const [discount, setDiscount] = useState(null)

	const [couponDiscountValue, setCouponDiscountValue] = useState(0)
	const [deliveryDiscountValue, setDeliveryDiscountValue] = useState(0)
	const [discountValue, setDiscountValue] = useState(0)
	const [discountProducts, setDiscountProducts] = useState([])
	const [ohneAktionsprodukte, setOhneAktionsprodukte] = useState(false)

	const [paymentOption, setPaymentOption] = useState('PayPal')

	useEffect(() => {
		if (
			deliveryOption?.key === 'Werksverkauf'
			// || paymentOption === 'Banküberweisung'
		) {
			setDeliveryDiscount(0.05)
		} else {
			setDeliveryDiscount(null)
		}
	}, [deliveryOption, paymentOption])

	useEffect(() => {
		setPaymentOption((currentOption) => {
			if (deliveryOption?.key === 'Werksverkauf') return 'Werksverkauf'
			return currentOption === 'Werksverkauf' ? 'PayPal' : currentOption
		})
	}, [deliveryOption])

	useEffect(() => {
		// reset item.product_variant_discount on every item in cart to 0
		const newCart = [...cart]
		newCart.forEach((item) => {
			item.product_variant_discount = 0
		})
		setCart(newCart)
		if (couponDiscount) {
			if (couponDiscountVariant === 'Gutscheincode') {
				if (cartTotalPrice < couponDiscount) {
					setCouponDiscountValue(cartTotalPrice - deliveryDiscountValue)
				} else {
					setCouponDiscountValue(couponDiscount)
				}
			} else {
				if (discountProducts?.length > 0) {
					const formattedDiscountProducts = discountProducts.map((item) => ({
						produkt: item.produkt?.data?.id,
						groesse: item.groesse?.data?.attributes?.Name || ''
					}))
					// map through cart and check if there is a product with the same strapi_id and variant as the formattedDiscountProducts
					const discountProductsInCart = cart.filter((item) =>
						formattedDiscountProducts.find(
							(discountProduct) =>
								discountProduct.produkt === item.product_strapi_id &&
								discountProduct.groesse === item.product_variant
						)
					)

					discountProductsInCart.forEach((item) => {
						if (
							item.product_category === 'Gutschein' ||
							(ohneAktionsprodukte &&
								item.product_variant_price < item.product_variant_normal_price)
						) {
							item.product_variant_discount = 0
						} else {
							item.product_variant_discount =
								couponDiscount * item.product_variant_price
						}
					})

					// update local cart
					const newCart = [...cart]

					discountProductsInCart.forEach((discountItem) => {
						const productInCart = newCart.find(
							(cartItem) =>
								cartItem.product_strapi_id === discountItem.product_strapi_id &&
								cartItem.product_variant === discountItem.product_variant
						)
						if (productInCart) {
							productInCart.product_variant_discount =
								discountItem.product_variant_discount
						}
					})

					setCart(newCart)
				} else {
					if (ohneAktionsprodukte) {
						// map thorugh cart and if "product_variant_normal_price" is the same as "product_variant_price" then apply the discount and update product_variant_discount
						const newCart = [...cart]

						newCart.forEach((item) => {
							// nie rabattieren
							if (item.product_category === 'Gutschein') {
								return
							}

							if (
								item.product_variant_price === item.product_variant_normal_price
							) {
								item.product_variant_discount =
									couponDiscount * item.product_variant_price
							}
						})

						setCart(newCart)
					} else {
						const newCart = [...cart]

						newCart.forEach((item) => {
							// nie rabattieren
							if (item.product_category === 'Gutschein') {
								return
							}

							item.product_variant_discount =
								couponDiscount * item.product_variant_price
						})

						setCart(newCart)
					}
				}
			}
		} else {
			setCouponDiscountValue(0)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		ohneAktionsprodukte,
		couponDiscount,
		deliveryDiscountValue,
		cartTotalPrice,
		couponDiscountVariant,
		discountProducts
	])

	useEffect(() => {
		if (deliveryDiscount) {
			setDeliveryDiscountValue(
				Number((cartTotalPrice * deliveryDiscount).toFixed(2))
			)
		} else {
			setDeliveryDiscountValue(0)
		}
	}, [deliveryDiscount, cartTotalPrice])

	useEffect(() => {
		if (couponDiscount || deliveryDiscount) {
			setDiscount((couponDiscount + deliveryDiscount).toFixed(2))
		} else {
			setDiscount(null)
		}
	}, [couponDiscount, deliveryDiscount])

	useEffect(() => {
		if (discount) {
			setDiscountValue(
				Number((couponDiscountValue + deliveryDiscountValue).toFixed(2))
			)
		} else {
			setDiscountValue(0)
		}
	}, [discount, couponDiscountValue, deliveryDiscountValue, cartTotalPrice])

	useEffect(() => {
		// cartProductCount
		let count = 0
		cart.forEach((item) => {
			count += item.product_count
		})
		setCartProductCount(count)

		// cartTotalPrice
		let price = 0
		let discount = 0
		cart.forEach((item) => {
			price += item.product_variant_price * item.product_count
			discount += item.product_variant_discount * item.product_count
		})

		if (couponDiscountVariant !== 'Gutscheincode') {
			setCouponDiscountValue(discount)
		}
		setCartTotalPrice(price)
	}, [cart, couponDiscountVariant])

	useEffect(() => {
		setCart((currentCart) => {
			const reconciliation = removeIneligibleComplimentaryProducts(
				currentCart,
				gratisProduktAb,
				paymentOption
			)
			return reconciliation.removed.length ? reconciliation.cart : currentCart
		})
	}, [cart, gratisProduktAb, paymentOption])

	// FUNCTIONS
	const checkForCartID = async (isFreshCart = false) => {
		const result = await resolveCartUid({
			currentUid: cartIdRef.current,
			isFresh: isFreshCart,
			loadCart: getStrapiCart,
			createCart: createStrapiCartID
		})

		if (result.stale && !result.uid) {
			cartIdRef.current = null
			setCartID(null)
			setCart([])
			try { if (isBrowser) window.localStorage.removeItem('CartID') } catch { /* optional storage */ }
		}

		if (result.uid && result.uid !== cartIdRef.current) {
			createdCartUid.current = result.uid
			cartIdRef.current = result.uid
			setCartID(result.uid)
			try { if (isBrowser) window.localStorage.setItem('CartID', JSON.stringify(result.uid)) } catch { /* optional storage */ }
		}
		if (!result.uid) {
			checkoutLimitToast(
				'Der Warenkorb ist momentan nicht erreichbar. Bitte versuchen Sie es erneut.'
			)
		}

		return result.uid
	}

	const addProductToCart = async (
		product,
		variant,
		count,
		isFreshCart = false
	) => {
		setCartIsLoading(true)
		const newCart = isFreshCart ? [] : cartRef.current.map(item => ({ ...item }))

		const currentCartID = await checkForCartID(isFreshCart)

		if (currentCartID) {
			const productInCart = newCart.find(
				(item) =>
					item.product_strapi_id === product.strapi_id &&
					item.product_variant === variant?.groesse?.Name
			)
			const reservedQuantity = getReservedVariantQuantity(
				newCart,
				product.strapi_id,
				variant?.groesse?.strapi_id
			)
			if (!canAddLine(newCart, productInCart)) {
				checkoutLimitToast(`Maximal ${CHECKOUT_MAX_LINE_ITEMS} Warenkorbpositionen sind möglich.`)
				setCartIsLoading(false)
				return false
			}
			if (reservedQuantity + count > CHECKOUT_MAX_QUANTITY) {
				checkoutLimitToast(`Maximal ${CHECKOUT_MAX_QUANTITY} Stück derselben Variante sind möglich.`)
				setCartIsLoading(false)
				return false
			}

			const successfullAddedProduct = await addProductToStrapiCart(
				currentCartID,
				product,
				variant,
				count
			)

			if (successfullAddedProduct) {
				const trackItem = {
					id: product.strapi_id,
					name: product.Name,
					category: product.kategorie?.Name,
					variant: variant?.groesse?.Name,
					brand: product.marke?.Name,
					price: variant.Preis_Angebot || variant.Preis
				}

				TrackAddProductToCartEvent(trackItem, count)

				if (productInCart) {
					productInCart.product_count += count
				} else {
					let productVariantImage = product?.Vorschaubild?.url

					// filter through product variants and find the one with the same size as the selected one
					const productVariant = product?.Variante?.find(
						(item) => item.groesse?.Name === variant?.groesse?.Name
					)
					// if there is a product variant with the same size as the selected one, set the image to the product variant image
					if (productVariant) {
						if (productVariant?.Bildergalerie?.length > 0) {
							productVariantImage = productVariant?.Bildergalerie[0]?.url
						}
					}

					newCart.push({
						product_type: 'product',
						product_strapi_id: product.strapi_id,
						product_name: product.Name,
						product_slug: product.slug,
						product_edition: product?.edition?.Name,
						product_category: product?.kategorie?.Name,
						product_variant: variant?.groesse?.Name,
						product_variant_strapi_id: variant?.groesse?.strapi_id,
						product_variant_price: variant.Preis_Angebot || variant.Preis,
						product_variant_normal_price: variant.Preis,
						product_variant_discount: 0,
						product_count: count,
						product_image: productVariantImage
					})
				}
				setCart(newCart)
				setIsSidebarOpen(true)
			}
		}

		setCartIsLoading(false)
	}
	const addFreeProductToCart = async (
		product,
		productVariant,
		count,
		giftReason = COMPLIMENTARY_REASON.THRESHOLD,
		openSidebar = true
	) => {
		setCartIsLoading(true)
		const newCart = cartRef.current.map(item => ({ ...item }))

		const currentCartID = await checkForCartID()

		if (currentCartID) {
			const normalizedReason =
				giftReason === COMPLIMENTARY_REASON.BANK_TRANSFER
					? COMPLIMENTARY_REASON.BANK_TRANSFER
					: COMPLIMENTARY_REASON.THRESHOLD
			const giftInSlot = newCart.find(
				(item) =>
					item.product_type === 'freeProduct' &&
					getComplimentaryReason(item) === normalizedReason
			)
			if (giftInSlot) {
				checkoutLimitToast('Für diesen Geschenkplatz wurde bereits ein Produkt gewählt.')
				setCartIsLoading(false)
				return false
			}
			if (!canAddComplimentaryLine(newCart, null)) {
				checkoutLimitToast(`Maximal ${CHECKOUT_MAX_COMPLIMENTARY_LINE_ITEMS} Gratisprodukt-Positionen sind möglich.`)
				setCartIsLoading(false)
				return false
			}
			if (
				getReservedVariantQuantity(
					newCart,
					product.strapi_id,
					productVariant?.groesse?.strapi_id
				) + count > CHECKOUT_MAX_QUANTITY
			) {
				checkoutLimitToast(`Maximal ${CHECKOUT_MAX_QUANTITY} Stück derselben Variante sind möglich.`)
				setCartIsLoading(false)
				return false
			}
			let productVariantImage =
				productVariant?.Bildergalerie?.length > 0
					? productVariant?.Bildergalerie[0]?.url
					: product?.Vorschaubild?.url

			const freeProductCartItem = {
				product_type: 'freeProduct',
				product_strapi_id: product.strapi_id,
				product_name: product.Name + ' - Geschenk',
				product_slug: product.slug,
				product_edition: product?.edition?.Name,
				product_variant: productVariant?.groesse?.Name,
				product_variant_strapi_id: productVariant?.groesse?.strapi_id,
				product_variant_price: 0,
				product_variant_normal_price: 0,
				product_variant_discount: 0,
				product_count: count,
				product_image: productVariantImage,
				gift_reason: normalizedReason
			}
			newCart.push(freeProductCartItem)

			setCart(newCart)
			if (openSidebar) setIsSidebarOpen(true)
			setCartIsLoading(false)
			return true
		}

		setCartIsLoading(false)
		return false
	}

	const addBundleToCart = async (
		bundle,
		variantOne,
		variantTwo,
		count,
		isFreshCart = false
	) => {
		setCartIsLoading(true)
		const newCart = isFreshCart ? [] : cartRef.current.map(item => ({ ...item }))

		const currentCartID = await checkForCartID(isFreshCart)

		if (currentCartID) {
			const bundleInCart = newCart.find(
				(item) =>
					item.product_strapi_id === bundle.strapi_id &&
					item.product_variant_one === variantOne?.groesse?.Name &&
					item.product_variant_two === variantTwo?.groesse?.Name
			)
			if (!canAddLine(newCart, bundleInCart)) {
				checkoutLimitToast(`Maximal ${CHECKOUT_MAX_LINE_ITEMS} Warenkorbpositionen sind möglich.`)
				setCartIsLoading(false)
				return false
			}
			const bundleVariants = [
				{
					productId: bundle?.Produkt[0]?.produkt.strapi_id,
					variantId: variantOne?.groesse?.strapi_id,
					quantity: bundle?.Produkt[0]?.Anzahl * count
				},
				{
					productId: bundle?.Produkt[1]?.produkt.strapi_id,
					variantId: variantTwo?.groesse?.strapi_id,
					quantity: bundle?.Produkt[1]?.Anzahl * count
				}
			]
			const exceedsQuantity = bundleVariants.some(
				(entry, index) =>
					getReservedVariantQuantity(newCart, entry.productId, entry.variantId) +
					entry.quantity +
					(index === 1 &&
					bundleVariants[0].productId === entry.productId &&
					bundleVariants[0].variantId === entry.variantId
						? bundleVariants[0].quantity
						: 0) >
					CHECKOUT_MAX_QUANTITY
			)
			if (exceedsQuantity) {
				checkoutLimitToast(`Maximal ${CHECKOUT_MAX_QUANTITY} Stück derselben Variante sind möglich.`)
				setCartIsLoading(false)
				return false
			}

			const successfullAddedBundle = await addBundleToStrapiCart(
				currentCartID,
				bundle,
				variantOne?.groesse?.Name,
				variantTwo?.groesse?.Name,
				count
			)

			if (successfullAddedBundle) {
				const trackItem = {
					id: 'b_' + bundle.strapi_id,
					name: bundle.Name,
					category: bundle.kategorie?.Name,
					variant:
						variantOne?.groesse?.Name + ' + ' + variantTwo?.groesse?.Name,
					brand: bundle.marke?.Name,
					price: bundle.Preis
				}

				TrackAddProductToCartEvent(trackItem, count)

				if (bundleInCart) {
					bundleInCart.product_count += count
				} else {
					newCart.push({
						product_type: 'bundle',
						product_strapi_id: bundle.strapi_id,
						product_name: bundle.Name,
						product_edition: bundle?.edition?.Name,
						product_count: count,
						product_one_strapi_id: bundle?.Produkt[0]?.produkt.strapi_id,
						product_two_strapi_id: bundle?.Produkt[1]?.produkt.strapi_id,
						product_one_name: bundle?.Produkt[0]?.produkt.Name,
						product_two_name: bundle?.Produkt[1]?.produkt.Name,
						product_one_edition: bundle?.Produkt[0]?.produkt.edition.Name,
						product_two_edition: bundle?.Produkt[1]?.produkt.edition.Name,
						product_one_anzahl: bundle?.Produkt[0]?.Anzahl,
						product_two_anzahl: bundle?.Produkt[1]?.Anzahl,
						product_variant_one: variantOne?.groesse?.Name,
						product_variant_two: variantTwo?.groesse?.Name,
						product_variant_one_strapi_id: variantOne?.groesse?.strapi_id,
						product_variant_two_strapi_id: variantTwo?.groesse?.strapi_id,
						product_variant_price: bundle.Preis,
						product_one_image: bundle?.Produkt[0]?.produkt?.Vorschaubild?.url,
						product_two_image: bundle?.Produkt[1]?.produkt?.Vorschaubild?.url
					})
				}
				setCart(newCart)
				setIsSidebarOpen(true)
			}
		}

		setCartIsLoading(false)
	}

	const removeProductFromCart = async (variant, strapi_id) => {
		setCartIsLoading(true)

		const newCart = cartRef.current.map(item => ({ ...item }))

		const successfullRemovedProduct = await removeProductToStrapiCart(
			cartIdRef.current,
			strapi_id,
			variant
		)

		if (successfullRemovedProduct) {
			const productInCart = newCart.find(
				(item) =>
					item.product_strapi_id === strapi_id &&
					item.product_variant === variant
			)
			if (productInCart) {
				const index = newCart.indexOf(productInCart)
				newCart.splice(index, 1)
			}
			setCart(newCart)
		}
		setCartIsLoading(false)
	}

	const removeFreeProductFromCart = async (
		variant,
		strapi_id,
		giftReason = null
	) => {
		setCartIsLoading(true)

		const newCart = cartRef.current.map(item => ({ ...item }))

		const productInCart = newCart.find(
			(item) =>
				item.product_strapi_id === strapi_id &&
				item.product_variant === variant &&
				item.product_type === 'freeProduct' &&
				(!giftReason || getComplimentaryReason(item) === giftReason)
		)
		if (productInCart) {
			const index = newCart.indexOf(productInCart)
			newCart.splice(index, 1)
		}
		setCart(newCart)

		setCartIsLoading(false)
	}

	const removeBundleFromCart = async (strapi_id, variantOne, variantTwo) => {
		setCartIsLoading(true)

		const newCart = cartRef.current.map(item => ({ ...item }))

		const successfullRemovedBundle = await removeBundleToStrapiCart(
			cartIdRef.current,
			strapi_id,
			variantOne,
			variantTwo
		)

		if (successfullRemovedBundle) {
			const bundleInCart = newCart.find(
				(item) =>
					item.product_strapi_id === strapi_id &&
					item.product_variant_one === variantOne &&
					item.product_variant_two === variantTwo
			)
			if (bundleInCart) {
				const index = newCart.indexOf(bundleInCart)
				newCart.splice(index, 1)
			}
			setCart(newCart)
		}

		setCartIsLoading(false)
	}

	const decreaseProdcutInCart = async (variant, strapi_id) => {
		setCartIsLoading(true)
		const newCart = cartRef.current.map(item => ({ ...item }))

		const successfullDecreasedProduct = await decreaseProductToStrapiCart(
			cartIdRef.current,
			strapi_id,
			variant
		)

		if (successfullDecreasedProduct) {
			const productInCart = newCart.find(
				(item) =>
					item.product_strapi_id === strapi_id &&
					item.product_variant === variant
			)
			if (productInCart) {
				productInCart.product_count--
				if (productInCart.product_count === 0) {
					const index = newCart.indexOf(productInCart)
					newCart.splice(index, 1)
				}
			}
			setCart(newCart)
		}
		setCartIsLoading(false)
	}

	const decreaseBundleInCart = async (strapi_id, variantOne, variantTwo) => {
		setCartIsLoading(true)

		const newCart = cartRef.current.map(item => ({ ...item }))

		const successfullDecreasedBundle = await decreaseBundleToStrapiCart(
			cartIdRef.current,
			strapi_id,
			variantOne,
			variantTwo
		)

		if (successfullDecreasedBundle) {
			const bundleInCart = newCart.find(
				(item) =>
					item.product_strapi_id === strapi_id &&
					item.product_variant_one === variantOne &&
					item.product_variant_two === variantTwo
			)
			if (bundleInCart) {
				bundleInCart.product_count--
				if (bundleInCart.product_count === 0) {
					const index = newCart.indexOf(bundleInCart)
					newCart.splice(index, 1)
				}
			}
			setCart(newCart)
		}

		setCartIsLoading(false)
	}

	const increaseProdcutInCart = async (variant, strapi_id) => {
		setCartIsLoading(true)
		const newCart = cartRef.current.map(item => ({ ...item }))
		const productInCart = newCart.find(
			(item) =>
				item.product_strapi_id === strapi_id &&
				item.product_variant === variant
		)
		if (
			!productInCart ||
			getReservedVariantQuantity(
				newCart,
				strapi_id,
				productInCart.product_variant_strapi_id
			) + 1 > CHECKOUT_MAX_QUANTITY
		) {
			checkoutLimitToast(`Maximal ${CHECKOUT_MAX_QUANTITY} Stück derselben Variante sind möglich.`)
			setCartIsLoading(false)
			return false
		}

		const successfullIncreasedProduct = await increaseProductToStrapiCart(
			cartIdRef.current,
			strapi_id,
			variant
		)
		if (successfullIncreasedProduct) {
			if (productInCart) {
				productInCart.product_count++
			}
			setCart(newCart)
		}
		setCartIsLoading(false)
	}

	const increaseBundleInCart = async (strapi_id, variantOne, variantTwo) => {
		setCartIsLoading(true)

		const newCart = cartRef.current.map(item => ({ ...item }))
		const bundleInCart = newCart.find(
			(item) =>
				item.product_strapi_id === strapi_id &&
				item.product_variant_one === variantOne &&
				item.product_variant_two === variantTwo
		)
		if (!bundleInCart) {
			setCartIsLoading(false)
			return false
		}
		const additions = [
			[bundleInCart.product_one_strapi_id, bundleInCart.product_variant_one_strapi_id, bundleInCart.product_one_anzahl],
			[bundleInCart.product_two_strapi_id, bundleInCart.product_variant_two_strapi_id, bundleInCart.product_two_anzahl]
		]
		const exceedsQuantity = additions.some(([productId, variantId, quantity], index) =>
			getReservedVariantQuantity(newCart, productId, variantId) +
			quantity +
			(index === 1 && additions[0][0] === productId && additions[0][1] === variantId
				? additions[0][2]
				: 0) > CHECKOUT_MAX_QUANTITY
		)
		if (exceedsQuantity) {
			checkoutLimitToast(`Maximal ${CHECKOUT_MAX_QUANTITY} Stück derselben Variante sind möglich.`)
			setCartIsLoading(false)
			return false
		}

		const successfullIncreasedBundle = await increaseBundleToStrapiCart(
			cartIdRef.current,
			strapi_id,
			variantOne,
			variantTwo
		)
		if (successfullIncreasedBundle) {
			if (bundleInCart) {
				bundleInCart.product_count++
			}
			setCart(newCart)
		}
		setCartIsLoading(false)
	}

	const applyStockShortages = async (shortages) => {
		const reconciliation = reconcileStockAvailability(cartRef.current, shortages)
		const complimentary = removeIneligibleComplimentaryProducts(
			reconciliation.cart,
			gratisProduktAb,
			paymentOption
		)
		const changes = [
			...reconciliation.changes,
			...complimentary.removed.map((item) => ({
				item,
				name: item.product_name,
				previousQuantity: item.product_count,
				quantity: 0
			}))
		]
		if (!changes.length) return []

		setCartIsLoading(true)
		try {
			for (const change of reconciliation.changes) {
				const item = change.item
				if (item.product_type === 'freeProduct') continue

				if (item.product_type === 'bundle') {
					if (change.quantity === 0) {
						await removeBundleToStrapiCart(
							cartIdRef.current,
							item.product_strapi_id,
							item.product_variant_one,
							item.product_variant_two
						)
					} else {
						for (let count = item.product_count; count > change.quantity; count -= 1) {
							await decreaseBundleToStrapiCart(
								cartID,
								item.product_strapi_id,
								item.product_variant_one,
								item.product_variant_two
							)
						}
					}
				} else if (change.quantity === 0) {
					await removeProductToStrapiCart(
						cartID,
						item.product_strapi_id,
						item.product_variant
					)
				} else {
					for (let count = item.product_count; count > change.quantity; count -= 1) {
						await decreaseProductToStrapiCart(
							cartIdRef.current,
							item.product_strapi_id,
							item.product_variant
						)
					}
				}
			}
			setCart(complimentary.cart)
			return changes
		} finally {
			setCartIsLoading(false)
		}
	}

	const resetValues = ({ resetCapture = true }) => {
		if (resetCapture) {
			setCaptureCheckoutOrderOrderId(null)
			setCaptureCheckoutOrderPayerId(null)
			setCaptureCheckoutAttemptId(null)
			setCaptureCheckoutAccessToken(null)
			setCaptureCheckoutQuoteTotal(null)
			setIsCaptureCheckout(false)
		}

		resetCouponValues()
		setDeliveryDiscount(null)
		setPaymentOption('PayPal')
		setDiscount(null)
		setDeliveryMessage('')
		setDeliveryOption(null)
		setDiscountValue(0)
	}

	const resetCouponValues = useCallback(() => {
		setCouponDiscount(null)
		setCouponDiscountVariant(null)
		setCouponDiscountID(null)
		setCouponName(null)
		setDiscountProducts([])
		setOhneAktionsprodukte(false)
	}, [])

	const buyCartItems = async () => {
		// reset all
		resetValues({
			resetCapture: true
		})
		setDeliveryForm({
			email: '',
			telephone: '',
			deliveryCountry: 'DE',
			deliveryFirstName: '',
			deliveryLastName: '',
			deliveryCompany: '',
			deliveryStreet: '',
			deliveryAdditionalInformation: '',
			deliveryZipCode: '',
			deliveryCity: '',
			otherBillingAddress: false,
			billingCountry: 'DE',
			billingFirstName: '',
			billingLastName: '',
			billingCompany: '',
			billingStreet: '',
			billingAdditionalInformation: '',
			billingZipCode: '',
			billingCity: '',
			acceptTerms: false,
			acceptDHLCheck: false
		})

		// Reset cart
		setCart([])
		// Reset localstorage CartID
		try { if (isBrowser) window.localStorage.removeItem('CartID') } catch { /* optional storage */ }

		//  Delete Strapi Cart
		const completedCartUid = cartIdRef.current
		cartIdRef.current = null
		createdCartUid.current = null
		setCartID(null)
		if (completedCartUid) await deleteStrapiCart(completedCartUid)

		// Delete affiliate info
		try {
			sessionStorage.removeItem('affiliate_id')
			sessionStorage.removeItem('affiliate_ref')
		} catch { /* optional storage */ }

		// Check for inviting to newsletter
		const {
			email,
			telephone,
			billingFirstName,
			billingLastName,
			deliveryFirstName,
			deliveryLastName
		} = deliveryForm

		let name = ''
		let surname = ''

		if (billingFirstName && billingLastName) {
			name = billingFirstName
			surname = billingLastName
		} else {
			name = deliveryFirstName || ''
			surname = deliveryLastName || ''
		}

		if (email) {
			addCustomerToNewsletter({
				language: language,
				email: email,
				name: name,
				surname: surname,
				telephone: telephone
			})
		}
	}

	return (
		<AppContext.Provider
			value={{
				language,
				popUpIsOpen,
				checkoutPopUpIsOpen,
				showNavbar,
				isSidebarOpen,
				cart,
				cartIsLoading,
				cartProductCount,
				cartTotalPrice,
				applyCheckoutQuote,
				applyStockShortages: queueCartAction(applyStockShortages),
				isCaptureCheckout,
				captureCheckoutOrderPayerId,
				captureCheckoutOrderOrderId,
				captureCheckoutAttemptId,
				captureCheckoutAccessToken,
				captureCheckoutQuoteTotal,
				deliveryForm,
				deliveryMessage,
				deliveryOption,
				couponDiscountValue,
				couponDiscountID,
				couponDiscountVariant,
				couponName,
				deliveryDiscountValue,
				discountValue,
				ohneAktionsprodukte,
				versandkostenValue,
				versandkostenfreiAb,
				gratisProduktAb,
				gratisProdukte,
				upSalesProdukte,
				paymentOption,
				setPopUpIsOpen,
				setCheckoutPopUpIsOpen,
				setShowNavbar,
				resetValues,
				resetCouponValues,
				closeSidebar,
				handleSidebar,
				buyCartItems,
				addProductToCart: queueCartAction(addProductToCart),
				addFreeProductToCart: queueCartAction(addFreeProductToCart),
				addBundleToCart: queueCartAction(addBundleToCart),
				removeProductFromCart: queueCartAction(removeProductFromCart),
				removeFreeProductFromCart: queueCartAction(removeFreeProductFromCart),
				removeBundleFromCart: queueCartAction(removeBundleFromCart),
				decreaseProdcutInCart: queueCartAction(decreaseProdcutInCart),
				decreaseBundleInCart: queueCartAction(decreaseBundleInCart),
				increaseProdcutInCart: queueCartAction(increaseProdcutInCart),
				increaseBundleInCart: queueCartAction(increaseBundleInCart),
				setIsCaptureCheckout,
				setCaptureCheckoutOrderPayerId,
				setCaptureCheckoutOrderOrderId,
				setCaptureCheckoutAttemptId,
				setCaptureCheckoutAccessToken,
				setCaptureCheckoutQuoteTotal,
				setDeliveryForm,
				setDeliveryMessage,
				setDeliveryOption,
				setCouponDiscount,
				setCouponDiscountID,
				setCouponDiscountVariant,
				setCouponName,
				setOhneAktionsprodukte,
				setDiscountProducts,
				resetCart: queueCartAction(resetCart),
				setPaymentOption
			}}
		>
			{children}
		</AppContext.Provider>
	)
}
